import { useEffect, useRef, useState } from "react";
import { Rocket, ImagePlus, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const API = process.env.REACT_APP_BACKEND_URL;

const NewLaunch = ({ onSaved }) => {
  const ref = useRef(null);
  const [f, setF] = useState({ title: "", message: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const fd = new FormData(); fd.append("title", f.title); fd.append("message", f.message); fd.append("file", file);
    setBusy(true);
    try { await api.post("/launches", fd); toast.success("Launch saved. Check the preview, then send."); setF({ title: "", message: "" }); setFile(null); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid="launch-form">
      <div className="font-display text-lg font-semibold">New product launch</div>
      <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Product name, e.g. Ragi Dosa Batter" data-testid="launch-title-input" />
      <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Short message: what is new, price, offer" rows={3} data-testid="launch-message-input" />
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="launch-photo-file" />
      <Button type="button" variant="outline" onClick={() => ref.current?.click()} className="w-full rounded-full" data-testid="launch-photo-pick"><ImagePlus className="mr-1 h-4 w-4" />{file ? file.name : "Product photo"}</Button>
      <Button disabled={busy || !file || !f.title.trim() || !f.message.trim()} onClick={save} className="w-full rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="launch-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save launch"}</Button>
    </div>
  );
};

const LaunchCard = ({ l, audience, configured, onSent }) => {
  const send = async () => {
    if (!window.confirm(`Send "${l.title}" on WhatsApp to ${audience} customers and leads?`)) return;
    try { await api.post(`/launches/${l.id}/send`); toast.success("Sending started"); onSent(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-4 rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={`launch-${l.id}`}>
      <img src={`${API}/api/public/launches/${l.id}/photo`} alt={l.title} className="h-24 w-24 shrink-0 rounded-xl object-cover" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{l.title}</div>
        <div className="line-clamp-2 text-sm text-muted-foreground">{l.message}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <a href={`/launch/${l.id}`} target="_blank" rel="noreferrer" className="font-semibold text-[#D96B43] hover:underline" data-testid={`launch-preview-${l.id}`}>Preview page</a>
          {l.status === "draft" ? <Button size="sm" disabled={!configured} onClick={send} className="h-8 rounded-full bg-[#25D366] text-white hover:bg-[#20bd5a]" data-testid={`launch-send-${l.id}`}><Send className="mr-1 h-3.5 w-3.5" />Send to {audience}</Button>
            : <span className="text-muted-foreground" data-testid={`launch-status-${l.id}`}>{l.status === "sent" ? `Sent ${fmtDateTime(l.sent_at)} · ${l.sent} delivered, ${l.failed} failed` : "Sending..."}</span>}
        </div>
      </div>
    </div>
  );
};

export default function Launches() {
  const [data, setData] = useState(null);
  const load = () => api.get("/launches").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="launches-page">
      <PageHeader eyebrow="WhatsApp" title="New product launch" subtitle="Announce a new product with a photo to every active customer and open lead on WhatsApp in one click." />
      {data && !data.configured && <div className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="launch-template-pending">The product-launch WhatsApp template is waiting for Meta approval. You can prepare launches now and send once it is approved.</div>}
      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <NewLaunch onSaved={load} />
        <div className="space-y-3" data-testid="launch-list">
          {data?.rows.length === 0 && <div className="rounded-2xl border border-dashed border-[#E7E2D6] p-10 text-center text-sm text-muted-foreground"><Rocket className="mx-auto mb-2 h-6 w-6" />No launches yet</div>}
          {data?.rows.map((l) => <LaunchCard key={l.id} l={l} audience={data.audience} configured={data.configured} onSent={() => setTimeout(load, 3000)} />)}
        </div>
      </div>
    </div>
  );
}
